import React, { useState } from 'react';
import axios from 'axios';
import { Form, Formik } from 'formik';
import { sortBy } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import { Heading } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

import TEXTS from '../texts';
import type { SorIssue, SorResolveAttemptParams, SorUser } from '../types';
import { SorCommitStatus, SorResolution } from '../types';

interface SorResolveFormValues {
  outcome: SorResolution;
  sor_external_id: string;
  replay_as_user_id: string;
  note: string;
}

interface SorResolveFormProps {
  issue: SorIssue;
  /** The users the change can be made as; the requester is always offered first. */
  replayUsers: SorUser[];
  userName: (userId: string) => string;
  /** Rejects with the server's error when the resolve is refused. */
  onResolve: (params: SorResolveAttemptParams) => Promise<void>;
}

// The server's rules: an attempt the SoR answered for is held under the SoR's own reference, so only one it never
// answered for takes the reference the operator found there, and only that one can be resolved as not at the SoR.
const needsSorReference = (issue: SorIssue, outcome: SorResolution) =>
  outcome === SorResolution.CompletedAtSor && issue.status === SorCommitStatus.Pending;

const getOutcomeOptions = (issue: SorIssue) => [
  { text: TEXTS.resolve.completedAtSor, optionValue: SorResolution.CompletedAtSor },
  {
    text: TEXTS.resolve.notAtSor,
    optionValue: SorResolution.NotAtSor,
    disabled: issue.status === SorCommitStatus.RemoteDone,
  },
];

const getValidationSchema = (issue: SorIssue) =>
  Yup.object().shape({
    note: Yup.string().trim().required(TEXTS.resolve.required),
    sor_external_id: Yup.string().when('outcome', {
      is: (outcome: SorResolution) => needsSorReference(issue, outcome),
      then: (schema) => schema.trim().required(TEXTS.resolve.required),
    }),
  });

// The server replays the change as its requester unless named another user, and replays only a completed one.
const toResolveParams = (
  issue: SorIssue,
  { outcome, sor_external_id, replay_as_user_id, note }: SorResolveFormValues
): SorResolveAttemptParams => ({
  outcome,
  note: note.trim(),
  ...(needsSorReference(issue, outcome) && { sor_external_id: sor_external_id.trim() }),
  ...(outcome === SorResolution.CompletedAtSor &&
    replay_as_user_id !== issue.requested_by_user_id && { replay_as_user_id }),
});

const SorResolveForm: React.FC<SorResolveFormProps> = ({ issue, replayUsers, userName, onResolve }) => {
  const { classes } = useStyles();
  const [refusal, setRefusal] = useState<string | null>(null);
  const otherReplayUserIds = sortBy(
    replayUsers.map(({ id }) => id).filter((userId) => userId !== issue.requested_by_user_id),
    (userId) => userName(userId).toLowerCase()
  );

  const handleSubmit = async (values: SorResolveFormValues) => {
    setRefusal(null);
    try {
      await onResolve(toResolveParams(issue, values));
    } catch (error) {
      // A 409 says why the change could not be made, such as a requester who lacks the authority for it now; the
      // form keeps it so the operator can pick another user and resolve again.
      const message = axios.isAxiosError(error) && error.response?.status === 409 ? getNotifyUserMessage(error) : null;
      if (message) {
        setRefusal(message);
      } else {
        await reportAxiosError(error);
      }
    }
  };

  return (
    <Formik<SorResolveFormValues>
      initialValues={{
        outcome: SorResolution.CompletedAtSor,
        sor_external_id: '',
        replay_as_user_id: issue.requested_by_user_id,
        note: '',
      }}
      validationSchema={getValidationSchema(issue)}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, setFieldValue }) => {
        const isCompleted = values.outcome === SorResolution.CompletedAtSor;
        return (
          <Form className="flex flex-col gap-20 rounded-md border border-solid border-slate-600 p-24">
            <Heading variant={Heading.TYPES.H4}>{TEXTS.resolve.title}</Heading>
            <RadioButtonGroupFormik
              id="outcome"
              direction="row"
              options={getOutcomeOptions(issue)}
              showOnly={isSubmitting}
            />
            {needsSorReference(issue, values.outcome) && (
              <TextFieldFormik
                id="sor_external_id"
                label={TEXTS.resolve.sorReference}
                className={classes.textField}
                fullWidth
              />
            )}
            {isCompleted && (
              <SingleSelectField<string>
                id="replay_as_user_id"
                label={TEXTS.resolve.replayAs}
                className={classes.textField}
                fullWidth
                value={values.replay_as_user_id}
                onChange={(userId) => setFieldValue('replay_as_user_id', userId)}
                disabled={isSubmitting}
                helperText={TEXTS.resolve.replayAsHelper}
              >
                <MenuItem value={issue.requested_by_user_id}>
                  {TEXTS.resolve.requester(userName(issue.requested_by_user_id))}
                </MenuItem>
                {otherReplayUserIds.map((userId) => (
                  <MenuItem key={userId} value={userId}>
                    {userName(userId)}
                  </MenuItem>
                ))}
              </SingleSelectField>
            )}
            <TextFieldFormik id="note" label={TEXTS.resolve.note} className={classes.textField} fullWidth multiline />
            {refusal && (
              <AlertBanner
                alertType={AlertBanner.ALERT_TYPES.ERROR}
                withIcon
                note={
                  <div className="flex flex-col gap-4">
                    <span>{refusal}</span>
                    {isCompleted && <span>{TEXTS.resolve.pickAnotherUser}</span>}
                  </div>
                }
              />
            )}
            <div className="flex justify-end">
              <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
                {TEXTS.resolve.submit}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SorResolveForm;
